import i18n, { translate as t } from '@thoughtspot/i18n';
import { isDebugLogEnabled } from '@thoughtspot/logger';
import { getPDomDomain, isDevServerPdomHostEnabled, isInDebugMode, isNebulaDevCluster, muzeChartPhase1Enabled, muzeDonutChartEnabled, muzeScatterChartEnabled, muzeBubbleChartEnabled, muzeCandlestickChartEnabled, muzeFunnelChartEnabled, muzeHeatmapChartEnabled, muzeWaterfallChartEnabled, muzeParetoChartEnabled, } from '@thoughtspot/session-service';
import React from 'react';
import { IFrameComponent } from '../components/iframe/iframe.component';
import { PDomComponent } from '../components/p-dom/p-dom.component';
import { ChartType } from '../types/prism';
import { SameOriginIframeComponent } from '../components/same-origin-iframe/same-origin-iframe.component';
import { shouldLoadChartInSameOrigin } from './use-chart-frame.util';
function getMuzeFlagsExtraData(debug) {
    return {
        debug: String(debug),
        muzeChartPhase1Enabled: String(muzeChartPhase1Enabled()),
        muzeDonutChartEnabled: String(muzeDonutChartEnabled()),
        muzeScatterChartEnabled: String(muzeScatterChartEnabled()),
        muzeBubbleChartEnabled: String(muzeBubbleChartEnabled()),
        muzeCandlestickChartEnabled: String(muzeCandlestickChartEnabled()),
        muzeHeatmapChartEnabled: String(muzeHeatmapChartEnabled()),
        muzeWaterfallChartEnabled: String(muzeWaterfallChartEnabled()),
        muzeParetoChartEnabled: String(muzeParetoChartEnabled()),
        muzeFunnelChartEnabled: String(muzeFunnelChartEnabled()),
    };
}
function getIframeComponent(componentId, viz, frameTitle, onLoad, onError) {
    const debug = (isInDebugMode() && isDebugLogEnabled()) || isNebulaDevCluster();
    const chartIframeElRef = React.useRef(null);
    const elementId = `chart-app-${componentId}`;
    const manifest = viz.config?.customChartManifest;
    let targetFrameUrl = '';
    if (manifest.sourceUrl) {
        targetFrameUrl = `${manifest.sourceUrl}${manifest?.chartAppAccessToken
            ? `?chartAppAccessToken=${manifest.chartAppAccessToken}`
            : ''}`;
    }
    return {
        chartIframeElRef,
        elementId,
        targetFrameUrl,
        chartJSX: (React.createElement(IFrameComponent, { elementId: elementId, customChartAppUrl: targetFrameUrl, frameTitle: frameTitle, chartIframeElRef: chartIframeElRef, onIFrameLoadCallback: onLoad, onIFrameErrorCallback: onError, extraData: getMuzeFlagsExtraData(debug) })),
    };
}
export function chartTypeToSubdomain(chartType) {
    return chartType.toLowerCase().replace(/_/g, '-');
}
function getHostedChartComponent(componentId, viz, frameTitle, onLoad, onError, isPrintMode, isLiveboardContext) {
    const chartType = viz?.config?.chartType;
    const chartTypeLowerCase = String(chartType).toLowerCase();
    const shouldUseSameOrigin = shouldLoadChartInSameOrigin(chartType);
    const chartIframeElRef = React.useRef(null);
    const elementId = `hosted-chart-${componentId}`;
    const debug = isInDebugMode() && isDebugLogEnabled();
    if (shouldUseSameOrigin) {
        const targetFrameUrl = `${window.location.origin}?chartType=${chartTypeLowerCase}`;
        return {
            chartIframeElRef,
            elementId,
            targetFrameUrl,
            chartJSX: (React.createElement(SameOriginIframeComponent, { elementId: elementId, chartType: viz.config.chartType, frameTitle: frameTitle, chartIframeElRef: chartIframeElRef, onIFrameLoad: onLoad, onIFrameError: onError, debug: debug, extraData: {
                    ...getMuzeFlagsExtraData(debug),
                    chartType: chartTypeLowerCase
                } })),
        };
    }
    const domainSuffix = getPDomDomain();
    let targetFrameUrl = isLiveboardContext ?
        `https://hc-${chartTypeToSubdomain(String(chartType))}.${domainSuffix}` :
        `https://hc-advanced.${domainSuffix}?chartType=${chartTypeLowerCase}`;
    if (process.env.NODE_ENV === 'development' &&
        isDevServerPdomHostEnabled()) {
        targetFrameUrl = `${window.location.origin}/__pdom/?chartType=${chartTypeLowerCase}`;
    }
    return {
        chartIframeElRef,
        elementId,
        targetFrameUrl,
        chartJSX: (React.createElement(PDomComponent, { chartIframeElRef: chartIframeElRef, elementId: elementId, targetFrameUrl: targetFrameUrl, chartType: viz.config.chartType, frameTitle: frameTitle, disableOriginAgentCluster: !!isPrintMode, onLoad: onLoad, onError: onError, extraData: {
                ...getMuzeFlagsExtraData(debug),
                chartType: chartTypeLowerCase
            } })),
    };
}
function getHostedChartComponentForMobile(componentId, url, frameTitle, onLoad, onError) {
    const chartIframeElRef = React.useRef(null);
    const elementId = `chart-app-${componentId}`;
    const targetFrameUrl = url;
    const debug = isInDebugMode() && isDebugLogEnabled();
    return {
        chartIframeElRef,
        elementId,
        targetFrameUrl,
        chartJSX: (React.createElement(IFrameComponent, { elementId: elementId, customChartAppUrl: targetFrameUrl, frameTitle: frameTitle, chartIframeElRef: chartIframeElRef, onIFrameLoadCallback: onLoad, onIFrameErrorCallback: onError, extraData: {
                debug: String(debug),
                muzeChartPhase1Enabled: String(muzeChartPhase1Enabled()),
            } })),
    };
}
const CHART_TYPES_LABELLED_AS_IS = new Set([
    ChartType.AdvancedPivotTable,
    ChartType.CustomChart,
]);
function getChartTypeLabel(chartType) {
    const chartTypeKey = `chartTypes.${chartType}`;
    if (!i18n.exists(chartTypeKey))
        return t('customChart.iframeTitle');
    const label = t(chartTypeKey);
    return CHART_TYPES_LABELLED_AS_IS.has(chartType)
        ? label
        : t('customChart.iframeChartTypeLabel', { chartType: label });
}
function getFrameTitle(viz, vizTitle) {
    const chartTypeLabel = getChartTypeLabel(viz.config?.chartType);
    const title = vizTitle?.trim();
    return title
        ? t('customChart.iframeTitleWithVizTitle', {
            chartType: chartTypeLabel,
            title,
        })
        : chartTypeLabel;
}
export const useChartFrame = (componentId, viz, onLoad, onError, isPrintMode, isLiveboardContext, hostedChartUrlForMobile, vizTitle) => {
    const frameTitle = getFrameTitle(viz, vizTitle);
    if (hostedChartUrlForMobile) {
        return getHostedChartComponentForMobile(componentId, hostedChartUrlForMobile, frameTitle, onLoad, onError);
    }
    if (viz.config.chartType === ChartType.CustomChart) {
        return getIframeComponent(componentId, viz, frameTitle, onLoad, onError);
    }
    return getHostedChartComponent(componentId, viz, frameTitle, onLoad, onError, isPrintMode, isLiveboardContext);
};
//# sourceMappingURL=use-chart-frame.js.map